import { Layer, LayerOptions } from '../layers/Layer';
import type { Extent } from '../geometry/Extent';
import type { Source } from '../sources/Source';

// Lazy resolve Cesium so this module does not crash when bundled headlessly
let __Cesium: any = null;
function getCesium(): any {
  if (__Cesium) return __Cesium;
  try { __Cesium = require('cesium'); } catch {
    try { __Cesium = (globalThis as any).Cesium || null; } catch { __Cesium = null; }
  }
  return __Cesium;
}

export interface PointCloudShadingOptions {
  attenuation?: boolean;
  geometricErrorScale?: number;
  maximumAttenuation?: number;
  baseResolution?: number;
  eyeDomeLighting?: boolean;
  eyeDomeLightingStrength?: number;
  eyeDomeLightingRadius?: number;
}

export type ShadowMode = 'enabled' | 'disabled' | 'cast-only' | 'receive-only';
export type ColorBlendMode = 'highlight' | 'replace' | 'mix';
export type ClassificationType = 'terrain' | 'cesium-3d-tile' | 'both';
export type PointCloudColorMode = 'rgb' | 'classification' | 'elevation' | 'intensity' | 'single';

export interface Tileset3DLayerOptions extends LayerOptions {
  url: string;
  show?: boolean;
  maximumScreenSpaceError?: number;
  maximumMemoryUsage?: number;
  style?: any;
  colorBlendMode?: ColorBlendMode;
  colorBlendAmount?: number;
  luminanceAtZenith?: number;
  shadows?: ShadowMode;
  clippingPlanes?: any[];
  modelMatrix?: number[];
  debugShowBoundingVolume?: boolean;
  debugShowContentBoundingVolume?: boolean;
  debugShowGeometricError?: boolean;
  pointCloudShading?: PointCloudShadingOptions;
  classificationType?: ClassificationType;
  backFaceCulling?: boolean;
  enablePickFeatures?: boolean;
  preloadWhenHidden?: boolean;
  skipLevelOfDetail?: boolean;
  dynamicScreenSpaceError?: boolean;
  foveatedScreenSpaceError?: boolean;
  customShader?: any;
}

export class Tileset3DLayer extends Layer {
  public url: string;
  public show: boolean;
  public maximumScreenSpaceError: number;
  public maximumMemoryUsage: number;
  public style: any;
  public colorBlendMode: ColorBlendMode;
  public colorBlendAmount: number;
  public luminanceAtZenith: number;
  public shadows: ShadowMode;
  public clippingPlanes: any[];
  public modelMatrix?: number[];
  public debugShowBoundingVolume: boolean;
  public debugShowContentBoundingVolume: boolean;
  public debugShowGeometricError: boolean;
  public pointCloudShading?: PointCloudShadingOptions;
  public classificationType: ClassificationType;
  public backFaceCulling: boolean;
  public enablePickFeatures: boolean;
  public preloadWhenHidden: boolean;
  public skipLevelOfDetail: boolean;
  public dynamicScreenSpaceError: boolean;
  public foveatedScreenSpaceError: boolean;
  public customShader?: any;

  private _propertyNames: string[] = [];

  constructor(options: Tileset3DLayerOptions) {
    super(options);
    this.url = options.url;
    this.show = options.show !== false;
    this.maximumScreenSpaceError = options.maximumScreenSpaceError ?? 16;
    this.maximumMemoryUsage = options.maximumMemoryUsage ?? 512;
    this.style = options.style ?? null;
    this.colorBlendMode = options.colorBlendMode ?? 'highlight';
    this.colorBlendAmount = options.colorBlendAmount ?? 0.5;
    this.luminanceAtZenith = options.luminanceAtZenith ?? 0.2;
    this.shadows = options.shadows ?? 'enabled';
    this.clippingPlanes = options.clippingPlanes ?? [];
    this.modelMatrix = options.modelMatrix;
    this.debugShowBoundingVolume = options.debugShowBoundingVolume ?? false;
    this.debugShowContentBoundingVolume = options.debugShowContentBoundingVolume ?? false;
    this.debugShowGeometricError = options.debugShowGeometricError ?? false;
    this.pointCloudShading = options.pointCloudShading;
    this.classificationType = options.classificationType ?? 'both';
    this.backFaceCulling = options.backFaceCulling ?? true;
    this.enablePickFeatures = options.enablePickFeatures ?? true;
    this.preloadWhenHidden = options.preloadWhenHidden ?? false;
    this.skipLevelOfDetail = options.skipLevelOfDetail ?? false;
    this.dynamicScreenSpaceError = options.dynamicScreenSpaceError ?? false;
    this.foveatedScreenSpaceError = options.foveatedScreenSpaceError ?? false;
    this.customShader = options.customShader;
  }

  setStyle(style: any): void {
    this.style = style;
    if (this._cesiumPrimitive) {
      const Cesium = getCesium();
      if (Cesium?.Cesium3DTileStyle) {
        try { this._cesiumPrimitive.style = new Cesium.Cesium3DTileStyle(style); } catch {}
      }
    }
    this.emit('stylechange', { style });
  }

  getStyle(): any {
    return this.style;
  }

  applyStyle(conditions: any): void {
    const style = this.makeStyleFromConditions(
      Array.isArray(conditions) ? conditions : [conditions]
    );
    this.setStyle(style);
  }

  getBoundingVolume(): any {
    return null;
  }

  getStatistics(): {
    totalTilesLoaded: number;
    tilesTotalMemoryUsage: number;
    numberOfPendingRequests: number;
    numberOfTilesProcessing: number;
  } {
    return {
      totalTilesLoaded: 0,
      tilesTotalMemoryUsage: 0,
      numberOfPendingRequests: 0,
      numberOfTilesProcessing: 0,
    };
  }

  makeStyleFromConditions(
    conditions: Array<{ condition: string; style: string }>
  ): any {
    return {
      color: {
        conditions: conditions.map((c) => [c.condition, c.style]),
      },
    };
  }

  setClippingPlanes(planes: any[]): void {
    this.clippingPlanes = planes;
    this.emit('clippingplaneschange', { planes });
  }

  hasProperty(name: string): boolean {
    return this._propertyNames.includes(name);
  }

  getPropertyNames(): string[] {
    return [...this._propertyNames];
  }

  // ---- 3D scene attachment hooks (called by MISceneView) ----
  protected _attachedViewer: any = null;
  protected _cesiumPrimitive: any = null;

  _applyToScene(viewer: any): void {
    if (!viewer || this._cesiumPrimitive) return;
    const Cesium = getCesium();
    if (!Cesium) return;
    this._attachedViewer = viewer;
    try {
      const tileset = new Cesium.Cesium3DTileset({
        url: this.url,
        maximumScreenSpaceError: this.maximumScreenSpaceError,
        maximumMemoryUsage: this.maximumMemoryUsage,
        shadows: this._cesiumShadowMode(Cesium),
        show: this.visible && this.show,
      });
      this._cesiumPrimitive = viewer.scene.primitives.add(tileset);
      if (this.style && Cesium.Cesium3DTileStyle) {
        try { this._cesiumPrimitive.style = new Cesium.Cesium3DTileStyle(this.style); } catch {}
      }
    } catch (err) {
      this.emit('error', { error: err instanceof Error ? err : new Error(String(err)) });
    }
  }

  _removeFromScene(viewer: any): void {
    if (!viewer || !this._cesiumPrimitive) {
      this._attachedViewer = null;
      this._cesiumPrimitive = null;
      return;
    }
    try { viewer.scene.primitives.remove(this._cesiumPrimitive); } catch {}
    this._cesiumPrimitive = null;
    this._attachedViewer = null;
  }

  private _cesiumShadowMode(Cesium: any): any {
    const map: Record<string, any> = {
      'enabled': Cesium.ShadowMode?.ENABLED ?? 1,
      'disabled': Cesium.ShadowMode?.DISABLED ?? 0,
      'cast-only': Cesium.ShadowMode?.CAST_ONLY ?? 2,
      'receive-only': Cesium.ShadowMode?.RECEIVE_ONLY ?? 3,
    };
    return map[this.shadows] ?? map['enabled'];
  }

  setVisible(visible: boolean): void {
    super.setVisible(visible);
    if (this._cesiumPrimitive) {
      try { this._cesiumPrimitive.show = visible && this.show; } catch {}
    }
  }

  dispose(): void {
    if (this._attachedViewer) this._removeFromScene(this._attachedViewer);
    this.clippingPlanes = [];
    this.style = null;
    this.customShader = null;
    super.dispose();
  }
}

export interface PointCloudLayerOptions extends LayerOptions {
  url: string;
  pointSize?: number;
  maximumAttenuation?: number;
  baseResolution?: number;
  geometricErrorScale?: number;
  eyeDomeLighting?: boolean;
  eyeDomeLightingStrength?: number;
  eyeDomeLightingRadius?: number;
  style?: any;
  colorMode?: PointCloudColorMode;
  color?: string;
  classificationColors?: Record<number, string>;
  elevationRange?: [number, number];
  elevationColorRamp?: string[];
  clippingPlanes?: any[];
  maximumScreenSpaceError?: number;
  maximumMemoryUsage?: number;
}

export class PointCloudLayer extends Layer {
  public url: string;
  public pointSize: number;
  public maximumAttenuation: number;
  public baseResolution: number;
  public geometricErrorScale: number;
  public eyeDomeLighting: boolean;
  public eyeDomeLightingStrength: number;
  public eyeDomeLightingRadius: number;
  public style: any;
  public colorMode: PointCloudColorMode;
  public color: string;
  public classificationColors: Record<number, string>;
  public elevationRange: [number, number];
  public elevationColorRamp: string[];
  public clippingPlanes: any[];
  public maximumScreenSpaceError: number;
  public maximumMemoryUsage: number;

  private _classificationVisibility: Map<number, boolean> = new Map();

  constructor(options: PointCloudLayerOptions) {
    super(options);
    this.url = options.url;
    this.pointSize = options.pointSize ?? 2;
    this.maximumAttenuation = options.maximumAttenuation ?? 0;
    this.baseResolution = options.baseResolution ?? 0;
    this.geometricErrorScale = options.geometricErrorScale ?? 1;
    this.eyeDomeLighting = options.eyeDomeLighting ?? true;
    this.eyeDomeLightingStrength = options.eyeDomeLightingStrength ?? 1;
    this.eyeDomeLightingRadius = options.eyeDomeLightingRadius ?? 1;
    this.style = options.style ?? null;
    this.colorMode = options.colorMode ?? 'rgb';
    this.color = options.color ?? '#ffffff';
    this.classificationColors = options.classificationColors ?? {};
    this.elevationRange = options.elevationRange ?? [0, 1000];
    this.elevationColorRamp = options.elevationColorRamp ?? [];
    this.clippingPlanes = options.clippingPlanes ?? [];
    this.maximumScreenSpaceError = options.maximumScreenSpaceError ?? 16;
    this.maximumMemoryUsage = options.maximumMemoryUsage ?? 512;
  }

  setPointSize(size: number): void {
    this.pointSize = Math.max(0, size);
    this.emit('pointsizechange', { pointSize: this.pointSize });
  }

  getPointSize(): number {
    return this.pointSize;
  }

  setColorMode(mode: PointCloudColorMode): void {
    this.colorMode = mode;
    this._applyPointCloudShadingToPrimitive();
    this.emit('colormodechange', { colorMode: mode });
  }

  setStyle(style: any): void {
    this.style = style;
    if (this._cesiumPrimitive) {
      const Cesium = getCesium();
      if (Cesium?.Cesium3DTileStyle) {
        try { this._cesiumPrimitive.style = new Cesium.Cesium3DTileStyle(style); } catch {}
      }
    }
    this.emit('stylechange', { style });
  }

  setPointCloudShading(opts: PointCloudShadingOptions): void {
    if (opts.attenuation !== undefined && opts.maximumAttenuation !== undefined) {
      this.maximumAttenuation = opts.maximumAttenuation;
    }
    if (opts.geometricErrorScale !== undefined) this.geometricErrorScale = opts.geometricErrorScale;
    if (opts.eyeDomeLighting !== undefined) this.eyeDomeLighting = opts.eyeDomeLighting;
    if (opts.eyeDomeLightingStrength !== undefined) this.eyeDomeLightingStrength = opts.eyeDomeLightingStrength;
    if (opts.eyeDomeLightingRadius !== undefined) this.eyeDomeLightingRadius = opts.eyeDomeLightingRadius;
    this._applyPointCloudShadingToPrimitive();
    this.emit('shadingchange', { shading: opts });
  }

  private _applyPointCloudShadingToPrimitive(): void {
    if (!this._cesiumPrimitive?.pointCloudShading) return;
    try {
      const s = this._cesiumPrimitive.pointCloudShading;
      s.attenuation = this.maximumAttenuation > 0;
      if (this.maximumAttenuation > 0) s.maximumAttenuation = this.maximumAttenuation;
      s.geometricErrorScale = this.geometricErrorScale;
      s.eyeDomeLighting = this.eyeDomeLighting;
      s.eyeDomeLightingStrength = this.eyeDomeLightingStrength;
      s.eyeDomeLightingRadius = this.eyeDomeLightingRadius;
    } catch {}
  }

  setElevationRange(range: [number, number]): void {
    this.elevationRange = range;
    this.emit('elevationrangechange', { elevationRange: range });
  }

  setClassificationVisibility(classId: number, visible: boolean): void {
    this._classificationVisibility.set(classId, visible);
    this.emit('classificationvisibilitychange', { classId, visible });
  }

  getStatistics(): {
    pointCount: number;
    boundingBox: any;
    classifications: number[];
  } {
    return {
      pointCount: 0,
      boundingBox: null,
      classifications: Object.keys(this.classificationColors).map(Number),
    };
  }

  createCrossSection(
    line: [number, number][],
    width: number
  ): any {
    return {
      line,
      width,
      points: [],
    };
  }

  // ---- 3D scene attachment hooks (called by MISceneView) ----
  protected _attachedViewer: any = null;
  protected _cesiumPrimitive: any = null;

  _applyToScene(viewer: any): void {
    if (!viewer || this._cesiumPrimitive) return;
    const Cesium = getCesium();
    if (!Cesium) return;
    this._attachedViewer = viewer;
    try {
      const tileset = new Cesium.Cesium3DTileset({
        url: this.url,
        maximumScreenSpaceError: this.maximumScreenSpaceError,
        maximumMemoryUsage: this.maximumMemoryUsage,
        show: this.visible,
        pointCloudShading: {
          attenuation: this.maximumAttenuation > 0,
          maximumAttenuation: this.maximumAttenuation || undefined,
          baseResolution: this.baseResolution || undefined,
          geometricErrorScale: this.geometricErrorScale,
          eyeDomeLighting: this.eyeDomeLighting,
          eyeDomeLightingStrength: this.eyeDomeLightingStrength,
          eyeDomeLightingRadius: this.eyeDomeLightingRadius,
        },
      });
      this._cesiumPrimitive = viewer.scene.primitives.add(tileset);
    } catch (err) {
      this.emit('error', { error: err instanceof Error ? err : new Error(String(err)) });
    }
  }

  _removeFromScene(viewer: any): void {
    if (!viewer || !this._cesiumPrimitive) {
      this._attachedViewer = null;
      this._cesiumPrimitive = null;
      return;
    }
    try { viewer.scene.primitives.remove(this._cesiumPrimitive); } catch {}
    this._cesiumPrimitive = null;
    this._attachedViewer = null;
  }

  setVisible(visible: boolean): void {
    super.setVisible(visible);
    if (this._cesiumPrimitive) {
      try { this._cesiumPrimitive.show = visible; } catch {}
    }
  }

  dispose(): void {
    if (this._attachedViewer) this._removeFromScene(this._attachedViewer);
    this.clippingPlanes = [];
    this.style = null;
    this._classificationVisibility.clear();
    super.dispose();
  }
}

export interface InstancedModelLayerOptions extends LayerOptions {
  url: string;
  maximumScreenSpaceError?: number;
  maximumMemoryUsage?: number;
  shadows?: ShadowMode;
}

export class InstancedModelLayer extends Layer {
  public url: string;
  public maximumScreenSpaceError: number;
  public maximumMemoryUsage: number;
  public shadows: ShadowMode;

  protected _attachedViewer: any = null;
  protected _cesiumPrimitive: any = null;

  constructor(options: InstancedModelLayerOptions) {
    super(options);
    this.url = options.url;
    this.maximumScreenSpaceError = options.maximumScreenSpaceError ?? 16;
    this.maximumMemoryUsage = options.maximumMemoryUsage ?? 512;
    this.shadows = options.shadows ?? 'enabled';
  }

  _applyToScene(viewer: any): void {
    if (!viewer || this._cesiumPrimitive) return;
    const Cesium = getCesium();
    if (!Cesium) return;
    this._attachedViewer = viewer;
    try {
      const tileset = new Cesium.Cesium3DTileset({
        url: this.url,
        maximumScreenSpaceError: this.maximumScreenSpaceError,
        maximumMemoryUsage: this.maximumMemoryUsage,
        show: this.visible,
      });
      this._cesiumPrimitive = viewer.scene.primitives.add(tileset);
    } catch (err) {
      this.emit('error', { error: err instanceof Error ? err : new Error(String(err)) });
    }
  }

  _removeFromScene(viewer: any): void {
    if (viewer && this._cesiumPrimitive) {
      try { viewer.scene.primitives.remove(this._cesiumPrimitive); } catch {}
    }
    this._cesiumPrimitive = null;
    this._attachedViewer = null;
  }

  setVisible(visible: boolean): void {
    super.setVisible(visible);
    if (this._cesiumPrimitive) { try { this._cesiumPrimitive.show = visible; } catch {} }
  }

  dispose(): void {
    if (this._attachedViewer) this._removeFromScene(this._attachedViewer);
    super.dispose();
  }
}

export interface WMSLayerOptions extends LayerOptions {
  source: Source;
  transparent?: boolean;
}

export class WMSLayer extends Layer {
  public transparent: boolean;

  protected _attachedViewer: any = null;
  protected _cesiumImageryLayer: any = null;

  constructor(options: WMSLayerOptions) {
    super({ ...options, source: options.source });
    this.transparent = options.transparent !== false;
  }

  setTransparent(transparent: boolean): void {
    this.transparent = transparent;
    if (this._cesiumImageryLayer) {
      try { this._cesiumImageryLayer.alpha = transparent ? this.opacity : 1; } catch {}
    }
    this.emit('change');
  }

  isTransparent(): boolean {
    return this.transparent;
  }

  _applyToScene(viewer: any): void {
    if (!viewer || this._cesiumImageryLayer) return;
    const Cesium = getCesium();
    if (!Cesium) return;
    const src = this._source as any;
    if (!src) return;
    this._attachedViewer = viewer;
    try {
      const provider = new Cesium.WebMapServiceImageryProvider({
        url: src.url,
        layers: src.layers,
        parameters: { transparent: this.transparent, format: 'image/png', ...(src.params || {}) },
      });
      this._cesiumImageryLayer = viewer.imageryLayers.addImageryProvider(provider);
      this._cesiumImageryLayer.alpha = this.opacity;
      this._cesiumImageryLayer.show = this.visible;
    } catch (err) {
      this.emit('error', { error: err instanceof Error ? err : new Error(String(err)) });
    }
  }

  _removeFromScene(viewer: any): void {
    if (viewer && this._cesiumImageryLayer) {
      try { viewer.imageryLayers.remove(this._cesiumImageryLayer); } catch {}
    }
    this._cesiumImageryLayer = null;
    this._attachedViewer = null;
  }

  setVisible(visible: boolean): void {
    super.setVisible(visible);
    if (this._cesiumImageryLayer) { try { this._cesiumImageryLayer.show = visible; } catch {} }
  }

  setOpacity(opacity: number): void {
    super.setOpacity(opacity);
    if (this._cesiumImageryLayer) { try { this._cesiumImageryLayer.alpha = opacity; } catch {} }
  }

  dispose(): void {
    if (this._attachedViewer) this._removeFromScene(this._attachedViewer);
    super.dispose();
  }
}

export interface WMTSLayerOptions extends LayerOptions {
  source: Source;
}

export class WMTSLayer extends Layer {
  protected _attachedViewer: any = null;
  protected _cesiumImageryLayer: any = null;

  constructor(options: WMTSLayerOptions) {
    super({ ...options, source: options.source });
  }

  _applyToScene(viewer: any): void {
    if (!viewer || this._cesiumImageryLayer) return;
    const Cesium = getCesium();
    if (!Cesium) return;
    const src = this._source as any;
    if (!src) return;
    this._attachedViewer = viewer;
    try {
      const provider = new Cesium.WebMapTileServiceImageryProvider({
        url: src.url,
        layer: src.layer,
        style: src.style || 'default',
        format: src.format || 'image/png',
        tileMatrixSetID: src.tileMatrixSetID || src.matrixSet,
      });
      this._cesiumImageryLayer = viewer.imageryLayers.addImageryProvider(provider);
      this._cesiumImageryLayer.alpha = this.opacity;
      this._cesiumImageryLayer.show = this.visible;
    } catch (err) {
      this.emit('error', { error: err instanceof Error ? err : new Error(String(err)) });
    }
  }

  _removeFromScene(viewer: any): void {
    if (viewer && this._cesiumImageryLayer) {
      try { viewer.imageryLayers.remove(this._cesiumImageryLayer); } catch {}
    }
    this._cesiumImageryLayer = null;
    this._attachedViewer = null;
  }

  setVisible(visible: boolean): void {
    super.setVisible(visible);
    if (this._cesiumImageryLayer) { try { this._cesiumImageryLayer.show = visible; } catch {} }
  }

  setOpacity(opacity: number): void {
    super.setOpacity(opacity);
    if (this._cesiumImageryLayer) { try { this._cesiumImageryLayer.alpha = opacity; } catch {} }
  }

  dispose(): void {
    if (this._attachedViewer) this._removeFromScene(this._attachedViewer);
    super.dispose();
  }
}

export interface VideoLayerOptions extends LayerOptions {
  url: string;
  extent: Extent;
  autoplay?: boolean;
  loop?: boolean;
  muted?: boolean;
}

export class VideoLayer extends Layer {
  public url: string;
  public autoplay: boolean;
  public loop: boolean;
  public muted: boolean;

  private _playing: boolean = false;
  private _volume: number = 1;

  constructor(options: VideoLayerOptions) {
    super({ ...options, extent: options.extent });
    this.url = options.url;
    this.autoplay = options.autoplay ?? false;
    this.loop = options.loop ?? false;
    this.muted = options.muted ?? true;
    this._playing = this.autoplay;
  }

  isPlaying(): boolean {
    return this._playing;
  }

  setVolume(volume: number): void {
    this._volume = Math.max(0, Math.min(1, volume));
    this.emit('volumechange', { volume: this._volume });
  }

  getVolume(): number {
    return this._volume;
  }

  // ---- 3D scene attachment hooks (called by MISceneView) ----
  protected _attachedViewer: any = null;
  protected _cesiumPrimitive: any = null;
  protected _videoEl: HTMLVideoElement | null = null;

  _applyToScene(viewer: any): void {
    if (!viewer || this._cesiumPrimitive) return;
    const Cesium = getCesium();
    if (!Cesium || !this.extent) return;
    this._attachedViewer = viewer;
    try {
      const v = document.createElement('video');
      v.src = this.url;
      v.autoplay = this.autoplay;
      v.loop = this.loop;
      v.muted = this.muted;
      v.crossOrigin = 'anonymous';
      this._videoEl = v;
      const ext = this.extent as any;
      const [west, south, east, north] = [ext.minX ?? ext[0], ext.minY ?? ext[1], ext.maxX ?? ext[2], ext.maxY ?? ext[3]];
      const rect = Cesium.Rectangle.fromDegrees(west, south, east, north);
      const material = new Cesium.Material({
        fabric: { type: 'Image', uniforms: { image: v } },
      });
      const primitive = new Cesium.Primitive({
        geometryInstances: new Cesium.GeometryInstance({
          geometry: new Cesium.RectangleGeometry({ rectangle: rect }),
        }),
        appearance: new Cesium.MaterialAppearance({ material }),
        show: this.visible,
      });
      this._cesiumPrimitive = viewer.scene.primitives.add(primitive);
    } catch (err) {
      this.emit('error', { error: err instanceof Error ? err : new Error(String(err)) });
    }
  }

  _removeFromScene(viewer: any): void {
    if (viewer && this._cesiumPrimitive) {
      try { viewer.scene.primitives.remove(this._cesiumPrimitive); } catch {}
    }
    if (this._videoEl) { try { this._videoEl.pause(); this._videoEl.src = ''; } catch {} }
    this._cesiumPrimitive = null;
    this._videoEl = null;
    this._attachedViewer = null;
  }

  setVisible(visible: boolean): void {
    super.setVisible(visible);
    if (this._cesiumPrimitive) { try { this._cesiumPrimitive.show = visible; } catch {} }
  }

  play(): void {
    this._playing = true;
    if (this._videoEl) { try { this._videoEl.play(); } catch {} }
    this.emit('play');
  }

  pause(): void {
    this._playing = false;
    if (this._videoEl) { try { this._videoEl.pause(); } catch {} }
    this.emit('pause');
  }

  dispose(): void {
    if (this._attachedViewer) this._removeFromScene(this._attachedViewer);
    this.pause();
    super.dispose();
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Extra 3D layer types: imagery, GeoJSON data source, KML data source
// ─────────────────────────────────────────────────────────────────────────────

export interface ImageryLayer3DOptions extends LayerOptions {
  /** Provider URL (XYZ template like https://server/{z}/{x}/{y}.png) or a Cesium ImageryProvider */
  url?: string;
  provider?: any;
  brightness?: number;
  contrast?: number;
  hue?: number;
  saturation?: number;
  gamma?: number;
}

/** General-purpose 2D imagery overlay on the 3D globe. Wraps viewer.imageryLayers. */
export class ImageryLayer3D extends Layer {
  public providerUrl?: string;
  public providerInstance?: any;
  public brightness: number;
  public contrast: number;
  public hue: number;
  public saturation: number;
  public gamma: number;

  protected _attachedViewer: any = null;
  protected _cesiumImageryLayer: any = null;

  constructor(options: ImageryLayer3DOptions) {
    super(options);
    this.providerUrl = options.url;
    this.providerInstance = options.provider;
    this.brightness = options.brightness ?? 1;
    this.contrast = options.contrast ?? 1;
    this.hue = options.hue ?? 0;
    this.saturation = options.saturation ?? 1;
    this.gamma = options.gamma ?? 1;
  }

  _applyToScene(viewer: any): void {
    if (!viewer || this._cesiumImageryLayer) return;
    const Cesium = getCesium();
    if (!Cesium) return;
    this._attachedViewer = viewer;
    try {
      const provider = this.providerInstance ?? new Cesium.UrlTemplateImageryProvider({ url: this.providerUrl });
      this._cesiumImageryLayer = viewer.imageryLayers.addImageryProvider(provider);
      const il = this._cesiumImageryLayer;
      il.alpha = this.opacity;
      il.show = this.visible;
      il.brightness = this.brightness;
      il.contrast = this.contrast;
      il.hue = this.hue;
      il.saturation = this.saturation;
      il.gamma = this.gamma;
    } catch (err) {
      this.emit('error', { error: err instanceof Error ? err : new Error(String(err)) });
    }
  }

  _removeFromScene(viewer: any): void {
    if (viewer && this._cesiumImageryLayer) {
      try { viewer.imageryLayers.remove(this._cesiumImageryLayer); } catch {}
    }
    this._cesiumImageryLayer = null;
    this._attachedViewer = null;
  }

  setVisible(visible: boolean): void {
    super.setVisible(visible);
    if (this._cesiumImageryLayer) { try { this._cesiumImageryLayer.show = visible; } catch {} }
  }

  setOpacity(opacity: number): void {
    super.setOpacity(opacity);
    if (this._cesiumImageryLayer) { try { this._cesiumImageryLayer.alpha = opacity; } catch {} }
  }

  dispose(): void {
    if (this._attachedViewer) this._removeFromScene(this._attachedViewer);
    super.dispose();
  }
}

export interface GeoJsonDataSource3DOptions extends LayerOptions {
  /** GeoJSON object or URL string. */
  data: any;
  stroke?: string;
  fill?: string;
  strokeWidth?: number;
  markerSymbol?: string;
  clampToGround?: boolean;
}

/** Renders GeoJSON features as 3D entities via Cesium GeoJsonDataSource. */
export class GeoJsonDataSource3D extends Layer {
  public data: any;
  public stroke?: string;
  public fill?: string;
  public strokeWidth: number;
  public markerSymbol?: string;
  public clampToGround: boolean;

  protected _attachedViewer: any = null;
  protected _cesiumDataSource: any = null;

  constructor(options: GeoJsonDataSource3DOptions) {
    super(options);
    this.data = options.data;
    this.stroke = options.stroke;
    this.fill = options.fill;
    this.strokeWidth = options.strokeWidth ?? 2;
    this.markerSymbol = options.markerSymbol;
    this.clampToGround = options.clampToGround ?? true;
  }

  _applyToScene(viewer: any): void {
    if (!viewer || this._cesiumDataSource) return;
    const Cesium = getCesium();
    if (!Cesium) return;
    this._attachedViewer = viewer;
    try {
      const opts: any = { clampToGround: this.clampToGround, strokeWidth: this.strokeWidth };
      if (this.stroke) opts.stroke = Cesium.Color.fromCssColorString(this.stroke);
      if (this.fill) opts.fill = Cesium.Color.fromCssColorString(this.fill);
      if (this.markerSymbol) opts.markerSymbol = this.markerSymbol;
      Cesium.GeoJsonDataSource.load(this.data, opts).then((ds: any) => {
        ds.show = this.visible;
        this._cesiumDataSource = ds;
        viewer.dataSources.add(ds);
      }).catch((err: any) => {
        this.emit('error', { error: err instanceof Error ? err : new Error(String(err)) });
      });
    } catch (err) {
      this.emit('error', { error: err instanceof Error ? err : new Error(String(err)) });
    }
  }

  _removeFromScene(viewer: any): void {
    if (viewer && this._cesiumDataSource) {
      try { viewer.dataSources.remove(this._cesiumDataSource, true); } catch {}
    }
    this._cesiumDataSource = null;
    this._attachedViewer = null;
  }

  setVisible(visible: boolean): void {
    super.setVisible(visible);
    if (this._cesiumDataSource) { try { this._cesiumDataSource.show = visible; } catch {} }
  }

  setData(data: any): void {
    this.data = data;
    if (this._attachedViewer) {
      const v = this._attachedViewer;
      this._removeFromScene(v);
      this._applyToScene(v);
    }
  }

  dispose(): void {
    if (this._attachedViewer) this._removeFromScene(this._attachedViewer);
    super.dispose();
  }
}

export interface KmlDataSource3DOptions extends LayerOptions {
  /** KML URL or string. */
  data: any;
  clampToGround?: boolean;
  screenOverlayContainer?: HTMLElement;
}

/** Renders a KML/KMZ document as 3D entities via Cesium KmlDataSource. */
export class KmlDataSource3D extends Layer {
  public data: any;
  public clampToGround: boolean;
  public screenOverlayContainer?: HTMLElement;

  protected _attachedViewer: any = null;
  protected _cesiumDataSource: any = null;

  constructor(options: KmlDataSource3DOptions) {
    super(options);
    this.data = options.data;
    this.clampToGround = options.clampToGround ?? true;
    this.screenOverlayContainer = options.screenOverlayContainer;
  }

  _applyToScene(viewer: any): void {
    if (!viewer || this._cesiumDataSource) return;
    const Cesium = getCesium();
    if (!Cesium) return;
    this._attachedViewer = viewer;
    try {
      const opts: any = {
        camera: viewer.scene.camera,
        canvas: viewer.scene.canvas,
        clampToGround: this.clampToGround,
      };
      if (this.screenOverlayContainer) opts.screenOverlayContainer = this.screenOverlayContainer;
      Cesium.KmlDataSource.load(this.data, opts).then((ds: any) => {
        ds.show = this.visible;
        this._cesiumDataSource = ds;
        viewer.dataSources.add(ds);
      }).catch((err: any) => {
        this.emit('error', { error: err instanceof Error ? err : new Error(String(err)) });
      });
    } catch (err) {
      this.emit('error', { error: err instanceof Error ? err : new Error(String(err)) });
    }
  }

  _removeFromScene(viewer: any): void {
    if (viewer && this._cesiumDataSource) {
      try { viewer.dataSources.remove(this._cesiumDataSource, true); } catch {}
    }
    this._cesiumDataSource = null;
    this._attachedViewer = null;
  }

  setVisible(visible: boolean): void {
    super.setVisible(visible);
    if (this._cesiumDataSource) { try { this._cesiumDataSource.show = visible; } catch {} }
  }

  dispose(): void {
    if (this._attachedViewer) this._removeFromScene(this._attachedViewer);
    super.dispose();
  }
}
